import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import instanciaAxios from './instanciaAxios';
import { refrescarLiteEnSegundoPlano } from './refrescarLite';

const ContextoSesion = createContext();

export const useContextoSesion = () => {
  return useContext(ContextoSesion);
};

export const ContextoSesionProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [sesionActiva, setSesionActiva] = useState(false);  
  const [cargando, setCargando] = useState(true);
  const [sesionCaja, setSesionCaja] = useState(null);
  const [sesionCajaCargada, setSesionCajaCargada] = useState(false);

  const navigate = useNavigate();
  
  // Inactividad: 1 hora
  const TIEMPO_INACTIVIDAD = 60 * 60 * 1000;
  // Throttle: solo actualizar lastActivity una vez cada 30 segundos
  const THROTTLE_MS = 30_000;
  const ultimaActualizacionRef = useRef(0);

  // Verificar sesión de caja activa
  const verificarSesionCaja = useCallback(async () => {
    try {
      const response = await instanciaAxios.get('/sesion-caja/activa');
      const sesion = response.data.sesion || null;
      // Estampar momento de fetch para cálculo correcto de tiempo transcurrido
      // (evita desfase de timezone entre servidor y navegador)
      if (sesion) {
        sesion._fetchedAt = Date.now();
      }
      setSesionCaja(sesion);
      return sesion;
    } catch (error) {
      console.log('Error al verificar sesión de caja:', error);
      setSesionCaja(null);
      return null;
    } finally {
      setSesionCajaCargada(true);
    }
  }, []);

  // Cerrar sesión - estabilizado con useCallback
  const cerrarSesionRef = useRef(null);
  const cerrarSesion = useCallback((mensaje = null) => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('permisos');
    setToken(null);
    setUsuario(null);
    setPermisos([]);
    setSesionActiva(false);
    setSesionCaja(null);
    
    if (mensaje) {
      toast.warning(mensaje);
    }
    
    navigate('/');
  }, [navigate]);
  // Mantener ref actualizada para uso en efectos
  cerrarSesionRef.current = cerrarSesion;

  // Registrar actividad del usuario (throttled)
  const registrarActividad = useCallback(() => {
    const ahora = Date.now();
    if (ahora - ultimaActualizacionRef.current > THROTTLE_MS) {
      ultimaActualizacionRef.current = ahora;
      localStorage.setItem('lastActivity', ahora.toString());
    }
  }, []);

  // Helper para verificar inactividad contra localStorage
  const verificarInactividad = useCallback(() => {
    const last = parseInt(localStorage.getItem('lastActivity') || '0');
    if (last > 0 && Date.now() - last > TIEMPO_INACTIVIDAD) {
      cerrarSesionRef.current('Sesión cerrada por inactividad (1 hora sin actividad)');
      return true;
    }
    return false;
  }, [TIEMPO_INACTIVIDAD]);

  // Helper: tiene permiso
  const tienePermiso = useCallback((clave) => {
    return permisos.includes(clave);
  }, [permisos]);

  // Iniciar sesión
  const iniciarSesion = useCallback(async (newToken, permisosUsuario = []) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('lastActivity', Date.now().toString());
    localStorage.setItem('permisos', JSON.stringify(permisosUsuario));
    ultimaActualizacionRef.current = Date.now();
    
    const decodedToken = jwtDecode(newToken);
    setToken(newToken);
    setUsuario(decodedToken);
    setPermisos(permisosUsuario);
    setSesionActiva(true);

    // Verificar si hay sesión de caja activa
    // Token is already in localStorage (line 98), safe to call immediately
    verificarSesionCaja();

    // Mantiene OmniERP Lite al dia (no hace nada si REACT_APP_LITE_URL no esta configurada)
    refrescarLiteEnSegundoPlano({ forzar: true });
  }, [verificarSesionCaja]);

  // Validar sesión al montar el componente
  useEffect(() => {
    const restaurarSesion = async () => {
      const storedToken = localStorage.getItem('token');
      const lastActivity = localStorage.getItem('lastActivity');
      
      if (storedToken && lastActivity) {
        const tiempoTranscurrido = Date.now() - parseInt(lastActivity);
        
        // Si pasó más de 1 hora, cerrar sesión
        if (tiempoTranscurrido > TIEMPO_INACTIVIDAD) {
          cerrarSesionRef.current('Sesión cerrada por inactividad');
          setCargando(false);
          return;
        }

        // Token válido y dentro del tiempo
        try {
          const decodedToken = jwtDecode(storedToken);
          setToken(storedToken);
          setUsuario(decodedToken);
          setSesionActiva(true);
          ultimaActualizacionRef.current = Date.now();
          
          // Restaurar permisos inmediatos desde localStorage (UX: sin espera)
          try {
            const storedPermisos = JSON.parse(localStorage.getItem('permisos') || '[]');
            setPermisos(storedPermisos);
          } catch {
            if (decodedToken.permisos && Array.isArray(decodedToken.permisos)) {
              setPermisos(decodedToken.permisos);
            }
          }

          // Refrescar permisos desde el backend (fuente de verdad)
          try {
            const permisosResp = await instanciaAxios.get('/permiso/mis-permisos');
            const permisosFrescos = permisosResp.data?.permisos || [];
            setPermisos(permisosFrescos);
            localStorage.setItem('permisos', JSON.stringify(permisosFrescos));
          } catch (errPermisos) {
            console.warn('No se pudieron refrescar permisos desde el servidor:', errPermisos);
            // Se mantienen los de localStorage como fallback
          }

          // Verificar sesión de caja al restaurar sesión (esperar resultado)
          await verificarSesionCaja();
          refrescarLiteEnSegundoPlano();
        } catch (error) {
          console.log("Token inválido:", error);
          localStorage.removeItem('token');
          localStorage.removeItem('lastActivity');
          localStorage.removeItem('permisos');
          setSesionCajaCargada(true);
        }
      } else {
        // No hay sesión - marcar sesión caja como ya verificada (no hay)
        setSesionCajaCargada(true);
      }
      
      setCargando(false);
    };

    restaurarSesion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat cada 60s: verificar si lastActivity > TIEMPO_INACTIVIDAD
  // Mucho mas confiable que un solo setTimeout de 1 hora
  useEffect(() => {
    if (!sesionActiva) return;
    const intervalo = setInterval(() => {
      verificarInactividad();
    }, 60_000);
    return () => clearInterval(intervalo);
  }, [sesionActiva, verificarInactividad]);

  // visibilitychange: verificar al volver al tab
  useEffect(() => {
    if (!sesionActiva) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        verificarInactividad();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [sesionActiva, verificarInactividad]);

  // Detectar actividad del usuario (throttled)
  useEffect(() => {
    if (!sesionActiva) return;

    const eventos = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    eventos.forEach(evento => {
      document.addEventListener(evento, registrarActividad, { passive: true });
    });

    return () => {
      eventos.forEach(evento => {
        document.removeEventListener(evento, registrarActividad);
      });
    };
  }, [sesionActiva, registrarActividad]);

  const valorContexto = {
    token,
    usuario,
    permisos,
    tienePermiso,
    sesionActiva,
    iniciarSesion,
    cerrarSesion,
    cargando,
    sesionCaja,
    setSesionCaja,
    verificarSesionCaja,
    sesionCajaCargada,
  };
  return (
    <ContextoSesion.Provider value={valorContexto}>
      {children}
    </ContextoSesion.Provider>
  );
};
